/** Traveller row with optional checkout selection sequence (not sent to API). */
export interface TravellerWithSelectionOrder {
  selectionOrder?: number;
  isSelected?: boolean;
}

/** Highest assigned `selectionOrder`, or -1 when none exist. */
export function getMaxSelectionOrder(
  travellers: TravellerWithSelectionOrder[]
): number {
  return travellers.reduce((max, traveller) => {
    const order = traveller.selectionOrder;
    return order != null && order >= 0 ? Math.max(max, order) : max;
  }, -1);
}

/** Next `selectionOrder` for a newly selected or manually added traveller. */
export function getNextSelectionOrder(
  travellers: TravellerWithSelectionOrder[]
): number {
  return getMaxSelectionOrder(travellers) + 1;
}

/**
 * Assigns sequential `selectionOrder` to pre-selected travellers on page load.
 * Restores cached values when present; returns the next counter value.
 */
export function assignSelectionOrdersToPrefilledTravellers<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): { travellers: T[]; nextCounter: number } {
  let nextCounter = 0;
  const result = travellers.map((traveller) => {
    if (traveller.selectionOrder != null && traveller.selectionOrder >= 0) {
      nextCounter = Math.max(nextCounter, traveller.selectionOrder + 1);
      return traveller;
    }
    if (traveller.isSelected) {
      const withOrder = { ...traveller, selectionOrder: nextCounter };
      nextCounter += 1;
      return withOrder;
    }
    return traveller;
  });
  return { travellers: result, nextCounter };
}

/** Selected travellers first (ascending `selectionOrder`), then unselected. */
export function reorderTravellersBySelectionOrder<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): T[] {
  const selected = travellers
    .filter((traveller) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );
  const unselected = travellers.filter((traveller) => !traveller.isSelected);
  return [...selected, ...unselected];
}

/** Selected travellers sorted by `selectionOrder` for checkout payload. */
export function getSelectedTravellersSortedBySelectionOrder<
  T extends TravellerWithSelectionOrder
>(travellers: T[]): T[] {
  return travellers
    .filter((traveller) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );
}

/**
 * Maps checkout `travellerId` (selection-order index) to the current form array index.
 */
export function findTravellerIndexByCheckoutId<
  T extends TravellerWithSelectionOrder
>(travellers: T[], checkoutTravellerId: number): number {
  const selectedWithIndex = travellers
    .map((traveller, index) => ({ traveller, index }))
    .filter(({ traveller }) => traveller.isSelected)
    .sort(
      (a, b) =>
        (a.traveller.selectionOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.traveller.selectionOrder ?? Number.MAX_SAFE_INTEGER)
    );

  const match = selectedWithIndex[checkoutTravellerId];
  if (match) return match.index;

  return checkoutTravellerId;
}

/** Assigns `selectionOrder` 0…n-1 to Excel-imported travellers (selection batch). */
export function assignSelectionOrdersToExcelTravellers<T>(
  travellers: T[]
): T[] {
  return travellers.map((traveller, index) => ({
    ...traveller,
    selectionOrder: index,
  }));
}
